import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ItemEditPageConfig } from '@front-platform/content-builder';
import { itemEditPageResolveConfig } from '@front-platform/content-builder';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { map, pairwise, take, takeUntil, filter } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, input } from '@angular/core';

import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';

import {
  CartItemKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { DoodReconstructedCart, DoodReconstructedCartItem } from '@core/models/cart.model';

import { ItemEditModalComponent } from '@shared/modals/item-edit-modal/item-edit-modal.component';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { Paths } from '@config/paths.config';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface ItemEditPageRuntimeConfig {}

export type ItemEditPageConfigInput = Partial<
  Omit<ItemEditPageConfig, keyof ItemEditPageRuntimeConfig> & ItemEditPageRuntimeConfig
>;
export type ItemEditPageResolvedConfig = Omit<ItemEditPageConfig, keyof ItemEditPageRuntimeConfig> &
  Partial<ItemEditPageRuntimeConfig>;
export interface ItemEditPageContract extends ConfigComponentContract<
  ItemEditPageConfigInput,
  ItemEditPageResolvedConfig
> {}

@Component({
  selector: 'app-item-edit-page',
  template: ``,
  standalone: false,
})
export class ItemEditPageComponent implements OnInit, OnDestroy, ItemEditPageContract {
  readonly config = input<ItemEditPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<ItemEditPageConfig, ItemEditPageRuntimeConfig>(
    itemEditPageResolveConfig,
    () => ({ config: this.config() }),
  );
  private destroyed$ = new Subject<boolean>();

  constructor(
    private readonly cartService: CartService,
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cartStoreSelector: CartStoreSelector,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly analyticsService: AnalyticsService,
  ) {
    this.activatedRoute.params.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      this.loadCartItem(parseInt(params.itemId, 10));
    });
  }

  ngOnInit(): void {
    this.modalsService.open(ItemEditModalComponent.handle);
    this.cartStoreSelector.selectEditItem
      .pipe(
        pairwise(),
        filter(([previous, current]) => !!previous && !current),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
      });
  }

  ngOnDestroy(): void {
    this.modalsService.close(ItemEditModalComponent.handle);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private loadCartItem(itemId: number): void {
    this.cartService.loadShopsOfCart$().pipe(takeUntil(this.destroyed$)).subscribe();
    this.cartService
      .getCartLineItems$()
      .pipe(
        takeUntil(this.destroyed$),
        take(1),
        map(reconstructedCart => this.lookForItemInCart(itemId, reconstructedCart)),
        map(cartItem => {
          if (cartItem) {
            this.cartDispatcher.updateStore({ edit_item: cartItem });
            return;
          }

          this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
        }),
      )
      .subscribe();
  }

  private lookForItemInCart(
    itemId: number,
    reconstructedCart: DoodReconstructedCart[],
  ): null | DoodReconstructedCartItem {
    for (const cartOfShop of reconstructedCart) {
      const cartItem = cartOfShop[ReconstructedCartKeys.Items].find(
        elem => elem[ReconstructedCartItemKeys.Item][CartItemKeys.Id] === itemId,
      );
      if (cartItem) {
        return cartItem;
      }
    }

    return null;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ItemEditPageComponent, { 'keyAliases': {} });
